/**
 * 78. Service Tickets — ticket queue; SLA timer; assignment;
 * resolution; CSAT.
 */
import {
  Card,
  PageHeader,
  DataTable,
  Skeleton,
  StatusBadge,
  Badge,
} from "@/components/ui";
import { useApiList, useApiQuery } from "@/lib/api/hooks";
import { formatDateTime } from "@/lib/money";
import { CreateRecordButton } from "@/components/CreateRecordButton";

interface TicketRow {
  id: string;
  ticketNumber: string;
  subject: string;
  customer: string;
  priority: "Low" | "Medium" | "High" | "Urgent";
  slaDueAt: string | null;
  assignee: string | null;
  csat: number | null;
  status: "Open" | "InProgress" | "OnHold" | "Resolved" | "Closed";
}

interface LookupOption {
  id: string;
  name?: string;
  code?: string;
}

export function ServiceTicketsPage() {
  const customers = useApiQuery<LookupOption[]>(["customers"], "/customers");
  const { data, isLoading } = useApiList<TicketRow>(
    ["service", "tickets"],
    "/service/tickets",
  );

  return (
    <div>
      <PageHeader
        title="Service Tickets"
        subtitle="Ticket queue, SLA timers, assignment, resolution, and CSAT."
        actions={
          <CreateRecordButton
            label="New ticket"
            path="/service/tickets"
            invalidate={["service", "tickets"]}
            // NUM-P0: the server numbers the ticket from its series, so no
            // number is typed; the route takes an Idempotency-Key, one per
            // dialog, so a second Save after a timeout cannot open two.
            idempotent
            fields={[
              { name: "title", label: "Subject", required: true },
              { name: "description", label: "Description" },
              {
                name: "priority",
                label: "Priority",
                type: "select",
                defaultValue: "Medium",
                options: [
                  { value: "Low", label: "Low" },
                  { value: "Medium", label: "Medium" },
                  { value: "High", label: "High" },
                  { value: "Critical", label: "Critical" },
                ],
              },
              {
                name: "customerId",
                label: "Customer",
                type: "select",
                options: (customers.data ?? []).map((c) => ({
                  value: c.id,
                  label: c.name ?? c.code ?? c.id,
                })),
              },
            ]}
          />
        }
      />
      {isLoading || !data ? (
        <Skeleton rows={10} />
      ) : (
        <Card title={`Tickets (${data.total})`}>
          <DataTable<TicketRow>
            rows={data.items}
            columns={[
              {
                key: "ticketNumber",
                label: "Ticket",
                render: (r) => (
                  <span className="atlas-mono">{r.ticketNumber}</span>
                ),
              },
              { key: "subject", label: "Subject" },
              { key: "customer", label: "Customer" },
              {
                key: "priority",
                label: "Priority",
                render: (r) => (
                  <Badge
                    tone={
                      r.priority === "Urgent" || r.priority === "High"
                        ? "danger"
                        : r.priority === "Medium"
                          ? "warning"
                          : "default"
                    }
                  >
                    {r.priority}
                  </Badge>
                ),
              },
              {
                key: "slaDueAt",
                label: "SLA due",
                render: (r) => (r.slaDueAt ? formatDateTime(r.slaDueAt) : "—"),
              },
              {
                key: "assignee",
                label: "Assignee",
                render: (r) => r.assignee ?? "—",
              },
              {
                key: "csat",
                label: "CSAT",
                render: (r) =>
                  // `!== null` let undefined through, which rendered the
                  // literal text "undefined/5" on every unrated ticket.
                  typeof r.csat === "number" ? `${r.csat}/5` : "—",
              },
              {
                key: "status",
                label: "Status",
                render: (r) =>
                  r.status === "Resolved" || r.status === "Closed" ? (
                    <StatusBadge status={r.status} />
                  ) : r.status === "InProgress" ? (
                    <Badge tone="primary">{r.status}</Badge>
                  ) : r.status === "OnHold" ? (
                    <Badge tone="warning">{r.status}</Badge>
                  ) : (
                    <Badge tone="warning">{r.status}</Badge>
                  ),
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
}
